import React, { useState, useEffect } from 'react';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Building2, Save, Crown, CheckCircle, GraduationCap, 
  Globe, MapPin, Users, Calendar, Award, Plus, X
} from 'lucide-react';

const OrganizationPage = () => {
  const { user, token } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [constants, setConstants] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    domain: '',
    institution_type: '',
    board_affiliation: [],
    established_year: '',
    student_count: '',
    staff_count: '',
    facilities: [],
    accreditations: [],
    website: '',
    address: '',
    description: ''
  });
  const [facilityInput, setFacilityInput] = useState('');
  const [accreditationInput, setAccreditationInput] = useState('');

  const hasOrg = !!user?.organization_id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const constantsRes = await axios.get(`${API}/education/constants`);
        setConstants(constantsRes.data);

        if (hasOrg) {
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const response = await axios.get(`${API}/organizations/${user.organization_id}`, { 
            headers, 
            withCredentials: true 
          });
          setOrganization(response.data);
          setFormData({
            name: response.data.name || '',
            logo: response.data.logo || '',
            domain: response.data.domain || '',
            institution_type: response.data.institution_type || '',
            board_affiliation: response.data.board_affiliation || [],
            established_year: response.data.established_year?.toString() || '',
            student_count: response.data.student_count || '',
            staff_count: response.data.staff_count || '',
            facilities: response.data.facilities || [],
            accreditations: response.data.accreditations || [],
            website: response.data.website || '',
            address: response.data.address || '',
            description: response.data.description || ''
          });
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user?.organization_id, hasOrg]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  const addFacility = () => {
    if (facilityInput.trim() && !formData.facilities.includes(facilityInput.trim())) {
      setFormData(prev => ({
        ...prev,
        facilities: [...prev.facilities, facilityInput.trim()]
      }));
      setFacilityInput('');
    }
  };

  const removeFacility = (facility) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.filter(f => f !== facility)
    }));
  };

  const addAccreditation = () => {
    if (accreditationInput.trim() && !formData.accreditations.includes(accreditationInput.trim())) {
      setFormData(prev => ({
        ...prev,
        accreditations: [...prev.accreditations, accreditationInput.trim()]
      }));
      setAccreditationInput('');
    }
  };

  const removeAccreditation = (acc) => {
    setFormData(prev => ({
      ...prev,
      accreditations: prev.accreditations.filter(a => a !== acc)
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Institution name is required');
      return;
    }

    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        ...formData,
        established_year: formData.established_year ? parseInt(formData.established_year) : null
      };
      
      if (hasOrg) {
        await axios.put(`${API}/organizations/${user.organization_id}`, payload, { 
          headers, 
          withCredentials: true 
        });
        toast.success('Institution profile updated!');
      } else {
        const response = await axios.post(`${API}/organizations`, payload, { 
          headers, 
          withCredentials: true 
        });
        setOrganization(response.data);
        toast.success('Institution created!');
        window.location.reload();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getTierBadge = (tier) => {
    const config = {
      basic: { label: 'Basic', className: 'bg-slate-100 text-slate-800' },
      pro: { label: 'Pro', className: 'bg-indigo-100 text-indigo-800' },
      enterprise: { label: 'Enterprise', className: 'bg-purple-100 text-purple-800' },
    };
    const c = config[tier] || config.basic;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const commonFacilities = [
    'Smart Classrooms', 'Science Labs', 'Computer Lab', 'Library',
    'Sports Ground', 'Swimming Pool', 'Auditorium', 'Cafeteria',
    'Transport', 'Medical Room', 'Art Room', 'Music Room'
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="organization-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            {hasOrg ? 'Institution Profile' : 'Create Institution'}
          </h1>
          <p className="text-slate-600 mt-1">
            {hasOrg ? 'Manage your school/institution profile and attract top educators' : 'Set up your institution to start posting jobs'}
          </p>
        </div>

        {/* Current Plan */}
        {hasOrg && organization && (
          <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <Crown className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Current Plan</p>
                    <div className="flex items-center gap-2">
                      {getTierBadge(organization.subscription_tier)}
                      {organization.subscription_status === 'active' && (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <Button variant="outline" onClick={() => window.location.href = '/pricing'}>
                  Upgrade Plan
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Building2 className="w-5 h-5 text-indigo-600" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Institution Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Delhi Public School"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="logo">Logo URL</Label>
                <Input
                  id="logo"
                  value={formData.logo}
                  onChange={(e) => handleChange('logo', e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Institution Type</Label>
                <Select value={formData.institution_type} onValueChange={(v) => handleChange('institution_type', v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {constants?.institution_types?.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">About the Institution</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe your institution, its vision, mission, and what makes it unique..."
                className="mt-1 min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="website">Website</Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="https://www.yourschool.edu.in"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="established">Established Year</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="established"
                    type="number"
                    value={formData.established_year}
                    onChange={(e) => handleChange('established_year', e.target.value)}
                    placeholder="e.g. 1995"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="address">Address</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Full address including city and state"
                  className="pl-10 min-h-[60px]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Academic Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <GraduationCap className="w-5 h-5 text-indigo-600" />
              Academic Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Board Affiliation */}
            <div>
              <Label>Board Affiliation</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {constants?.boards_curriculum?.map(board => (
                  <Badge
                    key={board}
                    variant={formData.board_affiliation.includes(board) ? "default" : "outline"}
                    className="cursor-pointer transition-all"
                    onClick={() => handleMultiSelect('board_affiliation', board)}
                  >
                    {board}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="students">Student Count</Label>
                <div className="relative mt-1">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="students"
                    value={formData.student_count}
                    onChange={(e) => handleChange('student_count', e.target.value)}
                    placeholder="e.g. 2000+"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="staff">Staff Count</Label>
                <div className="relative mt-1">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="staff"
                    value={formData.staff_count}
                    onChange={(e) => handleChange('staff_count', e.target.value)}
                    placeholder="e.g. 100+"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Facilities */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Facilities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {commonFacilities.map(facility => (
                <Badge
                  key={facility}
                  variant={formData.facilities.includes(facility) ? "default" : "outline"}
                  className="cursor-pointer transition-all"
                  onClick={() => handleMultiSelect('facilities', facility)}
                >
                  {facility}
                </Badge>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={facilityInput}
                onChange={(e) => setFacilityInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFacility())}
                placeholder="Add custom facility"
              />
              <Button variant="outline" onClick={addFacility}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {formData.facilities.filter(f => !commonFacilities.includes(f)).length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {formData.facilities.filter(f => !commonFacilities.includes(f)).map(facility => (
                  <Badge key={facility} variant="secondary" className="pl-3 pr-1">
                    {facility}
                    <button onClick={() => removeFacility(facility)} className="ml-2 hover:bg-slate-300 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Accreditations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Award className="w-5 h-5 text-indigo-600" />
              Accreditations & Awards
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={accreditationInput}
                onChange={(e) => setAccreditationInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAccreditation())}
                placeholder="Add accreditation or award (e.g. NAAC A+, ISO 9001)"
              />
              <Button variant="outline" onClick={addAccreditation}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {formData.accreditations.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.accreditations.map(acc => (
                  <Badge key={acc} variant="secondary" className="pl-3 pr-1 bg-amber-50 text-amber-800">
                    <Award className="w-3 h-3 mr-1" />
                    {acc}
                    <button onClick={() => removeAccreditation(acc)} className="ml-2 hover:bg-amber-200 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Career Page */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Career Page Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="domain">Custom Career Page Domain (Optional)</Label>
              <Input
                id="domain"
                value={formData.domain}
                onChange={(e) => handleChange('domain', e.target.value)}
                placeholder="careers.yourschool.edu.in"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Set up a custom domain for your career page (requires DNS configuration)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end pb-8">
          <Button onClick={handleSave} disabled={saving} size="lg">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : hasOrg ? 'Save Changes' : 'Create Institution'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OrganizationPage;
